"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.checkBasketProviderReady = checkBasketProviderReady;
exports.getEvents = exports.defaultActionEvents = void 0;
exports.poll = poll;
exports.removeDuplicates = void 0;
exports.useEvents = useEvents;
var BasketUpdated = _interopRequireWildcard(require("@ori-events/basket-updated"));
var RequestShoppingContext = _interopRequireWildcard(require("@ori-events/request-shopping-context"));
var _checkoutUtils = require("@ori/checkout-utils");
var _fetching = require("@ori/fetching");
var _react = require("react");
var _constants = require("./constants");
var _logger = require("./logger");
var _shoppingContextActions = require("./shoppingContextActions");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
const defaultActionEvents = exports.defaultActionEvents = [RequestShoppingContext.Name];
const removeDuplicates = original => original.filter((value, index) => original.indexOf(value) === index);

/**
 * Updates the custom event lists to be later connected to proper event handlers.
 * De-duplicates the lists & ensures the trigger events contain at least the request-shopping-context event.
 * @param eventNames Optional list of trigger action event names to be monitored with loading state.
 */
exports.removeDuplicates = removeDuplicates;
const getEvents = eventNames => {
  const failedEvents = [];
  const successEvents = [BasketUpdated.Name];
  const triggerEvents = removeDuplicates([...(eventNames ?? []), ...defaultActionEvents]);
  triggerEvents.forEach(value => {
    const {
      failedEvent,
      successEvent
    } = _shoppingContextActions.shoppingContextActions.get(value) ?? {};
    if (failedEvent) {
      failedEvents.push(failedEvent);
    }
    if (successEvent) {
      successEvents.push(successEvent);
    }
  });
  return {
    failedEvents: removeDuplicates(failedEvents),
    successEvents: removeDuplicates(successEvents),
    triggerEvents
  };
};
exports.getEvents = getEvents;
/** Starts shopping context loading. Should be called only once after the window.isBasketProviderListening flag is true. */
function startContextLoading() {
  const event = new CustomEvent(RequestShoppingContext.Name, {
    detail: {
      sourceComponent: _constants.PACKAGE_NAME
    }
  });
  setTimeout(() => {
    window.dispatchEvent(event);
  });
}

// Waiting for basket provider ready flag to become available.

let timeoutId;

/**
 * Polls for the isBasketProviderListening flag existence.
 * Then starts the shopping context loading via custom event.
 */
function poll(logDebug) {
  if (window.isBasketProviderListening === true) {
    logDebug('The isBasketProviderListening is set to true.');
    startContextLoading();
  } else {
    logDebug(`The isBasketProviderListening is not set to true. Waiting ${_constants.BASKET_PROVIDER_POLL_TIMEOUT} ms ...`);
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => poll(logDebug), _constants.BASKET_PROVIDER_POLL_TIMEOUT);
  }
}

/**
 * Checks if basket provider is ready for listening to events before running rest of event handlers' code.
 * The events may be called prematurely by components not polling correctly at start for ready flag.
 */
function checkBasketProviderReady(logDebug, event) {
  const isReady = Boolean(window.isBasketProviderListening);
  if (!isReady) {
    let message = 'The basket provider is not listening to events yet.';
    if (event) {
      message += 'This event was ignored by shopping context provider:';
      logDebug(message, event);
    } else {
      logDebug(message);
    }
  }
  return isReady;
}
/**
 * Connects event handlers to the shopping-context-related events.
 * The success handlers transfer the events' data to react state which can be consumed by child components.
 * @param eventNames Optional list of trigger action event names for which the isLoading state should be set.
 * @param parentComponentName Optional name of the parent component to be included in the log labels for easier debugging.
 * @returns Object with the current state of the shopping context.
 */
function useEvents(eventNames, parentComponentName) {
  const logLabel = parentComponentName ? `${_constants.LIBRARY_NAME} (${parentComponentName})` : _constants.LIBRARY_NAME;
  const logDebug = (0, _checkoutUtils.getDebugLogger)(logLabel, _constants.LOG_COLOR);
  const [isLoaded, setIsLoaded] = (0, _react.useState)(false);
  const [isLoading, setIsLoading] = (0, _react.useState)(false);
  const [shoppingContext, setShoppingContext] = (0, _react.useState)();
  const setupIsLoaded = (0, _react.useCallback)(value => {
    logDebug('Setting isLoaded state.', value);
    setIsLoaded(value);
  }, [logDebug, setIsLoaded]);
  const setupIsLoading = (0, _react.useCallback)(value => {
    logDebug('Setting isLoading state.', value);
    setIsLoading(value);
  }, [logDebug, setIsLoading]);
  const setupShoppingContext = (0, _react.useCallback)(value => {
    logDebug('Setting shopping context data.', value);
    setShoppingContext(value);
  }, [logDebug, setShoppingContext]);
  const currentEvents = (0, _react.useMemo)(() => getEvents(eventNames), [eventNames]);
  const failedActionHandler = (0, _react.useCallback)(event => {
    if (!checkBasketProviderReady(logDebug, event)) {
      return;
    }
    const error = new Error('Error loading shopping context');
    _logger.mainLogger.error(error, {
      message: event.detail.message
    });
    setupIsLoading(false);
    // TODO -- setLastError() - should we store error state for the last action?
  }, [logDebug, setupIsLoading]);
  const successActionHandler = (0, _react.useCallback)(event => {
    if (!checkBasketProviderReady(logDebug, event)) {
      return;
    }
    logDebug('Shopping context was successfully loaded.\n\tshoppingContext', event.detail);
    setupShoppingContext(event.detail);
    setupIsLoading(false);
    setupIsLoaded(true);
  }, [logDebug, setupIsLoaded, setupIsLoading, setupShoppingContext]);
  const triggerActionHandler = (0, _react.useCallback)(event => {
    if (!checkBasketProviderReady(logDebug, event)) {
      return;
    }
    const {
      sourceComponent
    } = event.detail ?? {};
    logDebug(`Loading shopping context was triggered via ${event.type} event${sourceComponent ? ` from ${sourceComponent}` : ''}.`);
    setupIsLoading(true);
  }, [logDebug, setupIsLoading]);
  (0, _react.useEffect)(() => {
    const {
      failedEvents,
      successEvents,
      triggerEvents
    } = currentEvents;
    const eventListeners = [];
    failedEvents.forEach(eventName => {
      eventListeners.push({
        eventName,
        eventListener: failedActionHandler
      });
      window.addEventListener(eventName, failedActionHandler);
    });
    successEvents.forEach(eventName => {
      eventListeners.push({
        eventName,
        eventListener: successActionHandler
      });
      window.addEventListener(eventName, successActionHandler);
    });
    triggerEvents.forEach(eventName => {
      eventListeners.push({
        eventName,
        eventListener: triggerActionHandler
      });
      window.addEventListener(eventName, triggerActionHandler);
    });
    logDebug('Event listeners are connected.');
    return () => {
      eventListeners.forEach(({
        eventName,
        eventListener
      }) => {
        window.removeEventListener(eventName, eventListener);
      });
      eventListeners.length = 0;
      logDebug('Old event listeners were disconnected.');
    };
  }, [currentEvents, logDebug, failedActionHandler, successActionHandler, triggerActionHandler]);

  // Polling for the basket provider ready flag to become available.
  // The useSWR is used to trigger the polling only once, even in react strict mode.
  (0, _fetching.useSWR)('poll', () => poll(logDebug), _checkoutUtils.basicSwrOptions);
  const contextValue = {
    isLoaded,
    isLoading,
    shoppingContext
  };
  logDebug('useEvents hook called.', {
    contextValue,
    currentEvents
  });
  return contextValue;
}
